import { DateTime } from 'luxon';
import { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { SelfServiceSessionService } from '../../../api/generated';
import { clearLocalStorageInstruction, selfServiceRoutes } from '../utils';
import {
  SelfServiceSession,
  selfServiceSessionKeys,
  useSelfServiceSession,
} from '../../../api/queries/selfService/selfServiceSession';
import { useQueryClient } from '@tanstack/react-query';

export interface SessionManager {
  session: SelfServiceSession | undefined;
  isSessionPending: boolean;
  handleSessionExit: () => Promise<void>;
  handleSessionExtend?: () => Promise<void>;
}

/**
 * Exposes a session and methods to exit or extend the session.
 * Handles automatic redirects for session conflicts, expiration, and status changes.
 * Monitors session status and redirects appropriately when the session state changes.
 */
export function useSessionManager(jobsiteId: string, sessionId: string): SessionManager {
  const navigate = useNavigate();
  const previousSessionStatusRef = useRef<SelfServiceSession['status'] | null>(null);
  const queryClient = useQueryClient();

  const {
    data: session,
    isPending: isSessionPending,
    isError: isSessionError,
  } = useSelfServiceSession({ jobsiteId, sessionId });

  const invalidateQueries = useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: selfServiceSessionKeys.read({ jobsiteId, sessionId }),
    });
  }, [queryClient, jobsiteId, sessionId]);

  // Handle API errors
  useEffect(() => {
    if (isSessionError) {
      clearLocalStorageInstruction(jobsiteId);
      navigate(selfServiceRoutes.sessionError(jobsiteId), { replace: true });
    }
  }, [isSessionError, navigate, jobsiteId]);

  useEffect(() => {
    if (!session?.status) {
      return;
    }

    const currentStatus = session.status;
    const previousStatus = previousSessionStatusRef.current;

    // Update the previous status reference for the next render.
    previousSessionStatusRef.current = currentStatus;

    // Navigate to the infopoint once the session is no longer active
    if (currentStatus !== 'active') {
      if (previousStatus === null || currentStatus !== previousStatus) {
        invalidateQueries();
        clearLocalStorageInstruction(jobsiteId);
        navigate(selfServiceRoutes.sessionInfopoint(jobsiteId, sessionId), { replace: true });
      }
    }
  }, [session, navigate, jobsiteId, sessionId, invalidateQueries]);

  // Effect to handle client-side session expiration timer
  useEffect(() => {
    if (session?.status === 'active' && session.sessionExpiresAt) {
      const now = DateTime.now();
      const expiresAt = session.sessionExpiresAt;
      const msUntilExpiry = expiresAt.diff(now).toMillis();

      if (msUntilExpiry > 0) {
        const timerId = setTimeout(() => {
          invalidateQueries();
          clearLocalStorageInstruction(jobsiteId);
          navigate(selfServiceRoutes.sessionInfopoint(jobsiteId, sessionId), { replace: true });
        }, msUntilExpiry);

        // Cleanup timeout if component unmounts or dependencies change
        return () => clearTimeout(timerId);
      } else {
        if (session.status === 'active') {
          invalidateQueries();
          clearLocalStorageInstruction(jobsiteId);
          navigate(selfServiceRoutes.sessionInfopoint(jobsiteId, sessionId), { replace: true });
        }
      }
    }
  }, [session, navigate, jobsiteId, sessionId, invalidateQueries]);

  async function handleSessionExit(): Promise<void> {
    return SelfServiceSessionService.selfServiceSessionControllerProcessSessionEvent({
      path: { jobsiteId, sessionId },
      body: { type: 'cancel' },
    }).then(() => {
      invalidateQueries();
      clearLocalStorageInstruction(jobsiteId);
      navigate(selfServiceRoutes.sessionInfopoint(jobsiteId, sessionId), { replace: true });
    });
  }

  async function handleSessionExtend(): Promise<void> {
    if (!session) {
      throw new Error('Session data is not available');
    }

    return SelfServiceSessionService.selfServiceSessionControllerProcessSessionEvent({
      path: { jobsiteId, sessionId },
      body: { type: 'extend' },
    }).then(async () => {
      await invalidateQueries();
    });
  }

  return {
    session,
    isSessionPending,
    handleSessionExit,
    handleSessionExtend: session && session.extended < 1 ? handleSessionExtend : undefined,
  };
}
